import { z } from "zod";
const decimal = (places) => z.string().regex(new RegExp(`^(0|[1-9]\\d{0,8})(\\.\\d{1,${places}})?$`));
const money = decimal(2);
const precise = decimal(4);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, "Expected a valid calendar date");
/** Nonnegative fixed-point arithmetic. Inputs must first pass the decimal schema. */
export function workflowDecimalUnits(value, places) {
    const [whole, fraction = ""] = value.split(".");
    return BigInt(whole) * 10n ** BigInt(places) + BigInt(fraction.padEnd(places, "0"));
}
const tracking = z.object({ TrackingCategoryID: z.string().uuid(), TrackingOptionID: z.string().uuid() }).strict();
const line = z.object({
    Description: z.string().trim().min(1).max(500),
    Quantity: precise.refine(value => Number(value) > 0, "Quantity must be positive"),
    UnitAmount: precise, LineAmount: money, TaxAmount: money,
    AccountCode: z.string().min(1).max(50), TaxType: z.string().min(1).max(64),
    Tracking: z.array(tracking).max(2).optional(),
}).strict().superRefine((value, context) => {
    if (![value.Quantity, value.UnitAmount].every(amount => precise.safeParse(amount).success) ||
        !money.safeParse(value.LineAmount).success)
        return;
    // Xero rounds an exclusive quantity × unit amount to two decimal places.
    const product = workflowDecimalUnits(value.Quantity, 4) * workflowDecimalUnits(value.UnitAmount, 4);
    if ((product + 500000n) / 1000000n !== workflowDecimalUnits(value.LineAmount, 2)) {
        context.addIssue({ code: "custom", path: ["LineAmount"], message: "LineAmount must equal rounded Quantity × UnitAmount" });
    }
    if (new Set(value.Tracking?.map(entry => entry.TrackingCategoryID)).size !== (value.Tracking?.length ?? 0)) {
        context.addIssue({ code: "custom", path: ["Tracking"], message: "Each tracking category may be assigned once" });
    }
});
/** Exact reviewed facts for one draft purchase bill; no payments, updates or automatic approval. */
export const workflowXeroBillSchema = z.object({
    Type: z.literal("ACCPAY"), Status: z.literal("DRAFT"),
    Contact: z.object({ ContactID: z.string().uuid() }).strict(),
    InvoiceNumber: z.string().trim().min(1).max(255), Date: date, DueDate: date,
    CurrencyCode: z.string().regex(/^[A-Z]{3}$/), LineAmountTypes: z.literal("Exclusive"),
    LineItems: z.array(line).min(1).max(100), SubTotal: money, TotalTax: money, Total: money,
}).strict().superRefine((value, context) => {
    if (value.LineItems.some(item => !line.safeParse(item).success) ||
        ![value.SubTotal, value.TotalTax, value.Total].every(amount => money.safeParse(amount).success))
        return;
    const net = value.LineItems.reduce((sum, item) => sum + workflowDecimalUnits(item.LineAmount, 2), 0n);
    const tax = value.LineItems.reduce((sum, item) => sum + workflowDecimalUnits(item.TaxAmount, 2), 0n);
    for (const [field, expected] of [["SubTotal", net], ["TotalTax", tax], ["Total", net + tax]]) {
        if (workflowDecimalUnits(value[field], 2) !== expected) {
            context.addIssue({ code: "custom", path: [field], message: `${field} must reconcile exactly with every line` });
        }
    }
});
